import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Divider, Drawer, DrawerProps, IconButton } from "@mui/material";
import { ReactNode } from "react";
import { ALIAS } from "../tokens/design-tokens";
import Flex from "./Flex";
import Text from "./Text";

type Position = "top" | "left" | "right" | "bottom";

type SideSheetDrawerProps = {
  title?: string;
  open: boolean;
  onClose: () => void;
  onHideSheet?: () => void;
  hideSheetLabel?: string;
  children: ReactNode;
  footerActions?: ReactNode;
  pos?: Position;
  w?: string | number;
  disableCloseOutside?: boolean;
  isCanvasBackground?: boolean;
  endIcon?: ReactNode;
  backButton?: boolean;
  onBack?: () => void;
  withCloseIcon?: boolean;
} & Omit<DrawerProps, "title">;

const SideSheetDrawer = ({
  open,
  title,
  onClose,
  onHideSheet,
  hideSheetLabel = "Hide Sheet",
  pos = "right",
  w = 607,
  footerActions,
  children,
  disableCloseOutside = false,
  isCanvasBackground = false,
  endIcon,
  backButton = false,
  onBack,
  withCloseIcon = true,
  ...others
}: SideSheetDrawerProps) => {
  const handleBack = onBack ?? onClose;
  return (
    <Drawer
      open={open}
      disableEscapeKeyDown
      onClose={disableCloseOutside ? undefined : onClose}
      anchor={pos}
      PaperProps={{
        sx: {
          width: w,
          backgroundColor: isCanvasBackground
            ? ALIAS.surface.canvas.slate.z1
            : "#fff",
          boxShadow: "none",
        },
      }}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal,
      })}
      {...others}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: ALIAS.surface.canvas.slate.z2,
          borderLeft: `1px solid ${ALIAS.outline.barelyGrey}`,
          padding: "12px",
        }}
      >
        <Flex justifyContent="end" alignItems="center" pr="16px">
          {/* Hide Sheet / Back Button */}
          {/* <Flex
            alignItems="center"
            gap="4px"
            sx={{ cursor: "pointer" }}
            onClick={onHideSheet ?? onClose}
          >
            {backButton ? (
              <ArrowBackIcon
                sx={{
                  fontSize: 16,
                  color: ALIAS.text.default,
                }}
              />
            ) : (
              <ArrowForwardIcon
                sx={{
                  fontSize: 16,
                  color: ALIAS.text.default,
                }}
              />
            )}
            <Text size="12px" c={ALIAS.text.default} lineHeight="14px">
              {backButton ? "Back" : hideSheetLabel}
            </Text>
          </Flex> */}

          {/* End Icon & Close Button */}
          <Flex alignItems="center" gap="8px">
            {endIcon}
            {withCloseIcon && (
              <Flex
                alignItems="center"
                gap="4px"
                sx={{ cursor: "pointer" }}
                onClick={onClose}
              >
                <CloseIcon
                  sx={{
                    fontSize: 16,
                    color: ALIAS.text.default,
                  }}
                />
                <Text size="12px" c={ALIAS.text.default} lineHeight="14px">
                  Close
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        {/* Title */}
        {title && (
          <Flex mt="8px" alignItems="center" gap="8px">
            {backButton && (
              <IconButton
                size="small"
                onClick={handleBack}
                sx={{ p: 0, color: ALIAS.text.darkGrey }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            <Text
              size="18px"
              fontWeight={400}
              c={ALIAS.text.darkGrey}
              lineHeight="22px"
            >
              {title}
            </Text>
          </Flex>
        )}
      </Box>

      <Divider sx={{ borderColor: ALIAS.outline.barelyGrey }} />

      {/* Content area with custom scrollbar gutters */}
      <Flex sx={{ flex: 1, minHeight: 0 }}>
        {/* Scrollable content */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: isCanvasBackground
              ? ALIAS.surface.canvas.slate.z1
              : "#fff",
            borderLeft: `1px solid ${ALIAS.outline.barelyGrey}`,
            borderRight: `1px solid ${ALIAS.outline.barelyGrey}`,
            p: "12px",
            overflow: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
          ref={(el: HTMLDivElement | null) => {
            if (!el) return;
            const vTrack = el.nextElementSibling?.querySelector(
              "[data-scrollbar='v-puck']"
            ) as HTMLElement | null;
            const hTrack = el.parentElement?.nextElementSibling?.querySelector(
              "[data-scrollbar='h-puck']"
            ) as HTMLElement | null;
            const vGutter = el.nextElementSibling as HTMLElement | null;
            const hGutter = el.parentElement
              ?.nextElementSibling as HTMLElement | null;
            const updateScrollbars = () => {
              const vRatio = el.clientHeight / el.scrollHeight;
              if (vGutter) {
                if (vRatio >= 1) {
                  vGutter.style.display = "none";
                } else {
                  vGutter.style.display = "flex";
                  if (vTrack) {
                    vTrack.style.display = "block";
                    vTrack.style.height = `${vRatio * 100}%`;
                    vTrack.style.top = `${(el.scrollTop / el.scrollHeight) * 100}%`;
                  }
                }
              }
              const hRatio = el.clientWidth / el.scrollWidth;
              if (hGutter) {
                if (hRatio >= 1) {
                  hGutter.style.display = "none";
                } else {
                  hGutter.style.display = "flex";
                  if (hTrack) {
                    hTrack.style.width = `${hRatio * 100}%`;
                    hTrack.style.left = `${(el.scrollLeft / el.scrollWidth) * 100}%`;
                  }
                }
              }
            };
            el.addEventListener("scroll", updateScrollbars);
            const observer = new ResizeObserver(updateScrollbars);
            observer.observe(el);
            if (el.firstElementChild) observer.observe(el.firstElementChild);
            updateScrollbars();
          }}
        >
          {children}
        </Box>

        {/* Vertical scrollbar gutter */}
        <Box
          sx={{
            width: "24px",
            backgroundColor: "#fff",
            borderRight: `1px solid ${ALIAS.outline.barelyGrey}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: "6px",
            py: "10px",
          }}
        >
          <Box
            sx={{
              flex: 1,
              width: "10px",
              position: "relative",
              borderRadius: "8px",
              p: "2px",
            }}
          >
            <Box
              data-scrollbar="v-puck"
              sx={{
                position: "absolute",
                width: "6px",
                backgroundColor: ALIAS.outline.barelyGrey,
                borderRadius: "4px",
                left: "2px",
              }}
            />
          </Box>
        </Box>
      </Flex>

      {/* Horizontal scrollbar gutter */}
      <Box
        sx={{
          height: "24px",
          backgroundColor: "#fff",
          display: "none",
          alignItems: "center",
          px: "12px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: "10px",
            position: "relative",
            borderRadius: "8px",
            p: "2px",
          }}
        >
          <Box
            data-scrollbar="h-puck"
            sx={{
              position: "absolute",
              height: "6px",
              backgroundColor: ALIAS.outline.barelyGrey,
              borderRadius: "4px",
              top: "2px",
            }}
          />
        </Box>
      </Box>

      {/* Footer */}
      {footerActions && (
        <>
          <Divider sx={{ borderColor: ALIAS.outline.barelyGrey }} />
          <Box
            sx={{
              backgroundColor: ALIAS.surface.canvas.slate.z2,
              borderLeft: `1px solid ${ALIAS.outline.barelyGrey}`,
              borderRight: `1px solid ${ALIAS.outline.barelyGrey}`,
              borderBottom: `1px solid ${ALIAS.outline.barelyGrey}`,
              padding: "12px",
            }}
          >
            <Flex alignItems="center" justifyContent="end" gap="10px">
              {footerActions}
            </Flex>
          </Box>
          <Divider sx={{ borderColor: ALIAS.outline.barelyGrey }} />
        </>
      )}
    </Drawer>
  );
};

export default SideSheetDrawer;
